import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import {
  Assessment,
  LocalHospital,
  People,
  Speed
} from '@mui/icons-material';
import { analyticsService } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const StatCard = ({ title, value, icon, color }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Card sx={{ height: '100%', bgcolor: isDark ? 'background.paper' : 'white', borderRadius: 2, boxShadow: 2 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: `${color}15`,
          color: color,
          width: 56,
          height: 56,
          borderRadius: '50%',
          mr: 2
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight="medium" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        // Clinicians only see their facility data, Admins see all unless filtered
        const facilityId = currentUser?.role === 'clinician' ? currentUser.facility : null;
        
        const [overviewData, trendsData, perfData] = await Promise.all([
          analyticsService.getOverview(facilityId),
          analyticsService.getTrends(30, facilityId),
          currentUser?.role === 'admin' ? analyticsService.getRadiologistPerformance(facilityId) : Promise.resolve([])
        ]);

        setOverview(overviewData);
        setTrends(trendsData);
        setPerformance(perfData);
      } catch (err) {
        console.error('Failed to load analytics:', err);
        setError('Failed to load dashboard metrics.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [currentUser]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight="bold">
          System Analytics
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Studies (PACS)"
            value={overview?.pacs.totalStudies || 0}
            icon={<Assessment fontSize="large" />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Orders Received (RIS)"
            value={overview?.ris.ordersReceived || 0}
            icon={<LocalHospital fontSize="large" />}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Turnaround Time"
            value={`${overview?.pacs.avgTatMinutes || 0} min`}
            icon={<Speed fontSize="large" />}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={overview?.system.totalUsers || 0}
            icon={<People fontSize="large" />}
            color={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Trends Chart */}
        <Grid item xs={12} lg={currentUser?.role === 'admin' ? 8 : 12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Activity Trends (Last 30 Days)
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={trends} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(tick) => tick.substring(5)} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="studies" name="PACS Studies" stroke={theme.palette.primary.main} strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="orders" name="RIS Orders" stroke={theme.palette.secondary.main} strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Radiologist Performance (Admin Only) */}
        {currentUser?.role === 'admin' && (
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 0, borderRadius: 2, boxShadow: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
              <Box p={3} pb={1}>
                <Typography variant="h6" fontWeight="bold">
                  Radiologist Performance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Top readers by volume
                </Typography>
              </Box>
              <Divider />
              <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Radiologist</TableCell>
                      <TableCell align="right">Reports</TableCell>
                      <TableCell align="right">Avg TAT (m)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performance.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell component="th" scope="row">
                          Dr. {row.name}
                        </TableCell>
                        <TableCell align="right">{row.reportsCompleted}</TableCell>
                        <TableCell align="right">{row.avgTatMinutes}</TableCell>
                      </TableRow>
                    ))}
                    {performance.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No performance data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard;
